import { ja as jaLocale } from 'date-fns/locale/ja'
import { useAtomValue } from 'jotai'
import type { ReactNode } from 'react'
import { LinkBehavior } from '~/components/Navigation/Link/LinkBehavior'
import { colorModeAtom } from '~/states/atoms/Theme'

import type { GlobalStylesProps } from '@mui/material/GlobalStyles'
import GlobalStyles from '@mui/material/GlobalStyles'
import type { LinkProps } from '@mui/material/Link'
import { blue } from '@mui/material/colors'
import { jaJP as coreJaJp } from '@mui/material/locale'
import type { ThemeOptions } from '@mui/material/styles'
import { ThemeProvider, alpha, createTheme, responsiveFontSizes } from '@mui/material/styles'
import { jaJP as dataGridJaJp } from '@mui/x-data-grid-pro/locales'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { jaJP as datePickerJaJp } from '@mui/x-date-pickers/locales'
// ピッカーのコンポーネント override を theme.components で使えるようにする型拡張
import type {} from '@mui/x-date-pickers/themeAugmentation'
import type {} from '@mui/x-date-pickers-pro/themeAugmentation'

// import { SiteCompetitiveTagSettingState } from '~/graphql'

const {
  palette: { augmentColor },
} = createTheme()

export const blueGrey = {
  25: '#f6f7f8',
  50: '#eceff1',
  75: '#e2e7ea',
  100: '#cfd8dc',
  200: '#b0bec5',
  300: '#90a4ae',
  400: '#78909c',
  500: '#607d8b',
  600: '#546e7a',
  700: '#455a64',
  800: '#37474f',
  900: '#263238',
  A100: '#cfd8dc',
  A200: '#b0bec5',
  A400: '#78909c',
  A700: '#455a64',
}

// @see https://stackoverflow.com/questions/50069724/how-to-add-custom-mui-palette-colors
const customPalette = {
  palette: {
    chart: {
      target: augmentColor({ color: { main: '#3673e8' } }),
      competitive: augmentColor({ color: { main: '#8ab4f8' } }),
    },
    graph: {
      positionStateBar: augmentColor({ color: { main: '#D1E0F9' } }),
      line: [
        '#236bf6', // 青
        '#1bc42e', // 緑
        '#df1f11', // 赤
        '#ee8c0d', // オレンジ
        '#be11e7', // 紫
        '#e91e63', // ピンク
      ].map((main) => augmentColor({ color: { main } })),
    },
    analyticsGraph: {
      blue: augmentColor({
        name: 'analyticsGraph.blue',
        color: { main: '#3673e8' },
      }),
      skyBlue: augmentColor({
        name: 'analyticsGraph.skyBlue',
        color: { main: '#8ab4f8' },
      }),
      babyBlue: augmentColor({
        name: 'analyticsGraph.babyBlue',
        color: { main: '#aed7ff' },
      }),
    },
    summary: {
      sessions: augmentColor({
        name: 'summary.sessions',
        color: { main: '#1e88e5' },
      }),
      cv: augmentColor({
        name: 'summary.cv',
        color: { main: '#512da8' },
      }),
      cvr: augmentColor({
        name: 'summary.cvr',
        color: { main: '#00897b' },
      }),
      totalUsers: augmentColor({
        name: 'summary.totalUsers',
        color: { main: '#512da8' },
      }),
      screenPageViews: augmentColor({
        name: 'summary.screenPageViews',
        color: { main: '#ef6c00' },
      }),
      engagementRate: augmentColor({
        name: 'summary.engagementRate',
        color: { main: '#e64a19' },
      }),
    },
    psi: {
      fast: augmentColor({
        name: 'psi.fast',
        color: {
          main: '#0c6',
          light: 'rgba(0,204,102,0.5)',
          dark: '#080',
        },
      }),
      average: augmentColor({
        name: 'psi.average',
        color: {
          main: '#fa3',
          light: 'rgba(255,170,51,0.5)',
          dark: '#c30',
        },
      }),
      slow: augmentColor({
        name: 'psi.slow',
        color: {
          main: '#f33',
          light: 'rgba(255,51,51,0.5)',
          dark: '#c00',
        },
      }),
    },

    // tagSettingState: {
    //   [SiteCompetitiveTagSettingState.NOT_SETTING]: augmentColor({
    //     color: { main: '#0d8bc4' },
    //   }),
    //   [SiteCompetitiveTagSettingState.DUPLICATION]: augmentColor({
    //     color: { main: '#108346' },
    //   }),
    //   [SiteCompetitiveTagSettingState.LONG]: augmentColor({
    //     color: { main: '#d6024d' },
    //   }),
    //   [SiteCompetitiveTagSettingState.SHORT]: augmentColor({
    //     color: { main: '#b75602' },
    //   }),
    //   [SiteCompetitiveTagSettingState.SAME_H1]: augmentColor({
    //     color: { main: '#9c27b0' },
    //   }),
    //   [SiteCompetitiveTagSettingState.DOUBLE]: augmentColor({
    //     color: { main: '#d61c5b' },
    //   }),
    // },
  },
}

export const breakpoints: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 750,
      lg: 1440,
      xl: 1920,

      mobile: 0,
      tablet: 750,
      laptop: 1024,
      desktop: 1200,
    },
  },
}

export const canalyLightThemeOptions: ThemeOptions = {
  ...breakpoints,

  typography: {
    fontFamilySansSerif: 'sans-serif',
    fontFamilyMonospace: 'monospace',
  },
  palette: {
    mode: 'light',
    standard: {
      light: blueGrey['25'],
      main: blueGrey['800'],
    },
    primary: {
      main: blueGrey['800'],
      contrastText: '#fff',
    },
    secondary: {
      light: blueGrey['300'],
      main: '#fff',
      dark: blueGrey['500'],
      contrastText: blueGrey['800'],
    },
    error: {
      main: '#e53e46',
    },
    warning: {
      main: '#e78518',
    },
    info: {
      // light: '#f1f9fa',
      main: '#229ddd',
    },
    success: {
      main: '#45ba8e',
    },
    border: {
      light: '#00000020',
      main: blueGrey['75'],
      dark: '#0000003b',
    },
    menu: {
      main: blue['100'],
      dark: blue['900'],
    },
    hover: {
      light: '#34abbf14',
      main: '#34abbf1f',
    },
  },

  components: {
    MuiAppBar: {
      defaultProps: {
        color: 'inherit',
      },
      styleOverrides: {
        root: {
          height: 44,
          boxSizing: 'content-box',
          borderBottomStyle: 'solid',
          borderBottomWidth: 1,
          borderColor: blueGrey['75'],

          '& .MuiToolbar-root': {
            color: blueGrey['800'],
            backgroundColor: '#fff',
            // boxSizing: 'border-box',
            height: 44,
            minHeight: 'auto',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        // zoom 環境では getBoundingClientRect 計測のインジケーター位置がずれるため非表示にし、
        // 選択タブ側の box-shadow 下線（MuiTab 参照）で代替する。
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          'fontSize': '1rem',
          'textTransform': 'none',
          // インジケーター代替の下線（レイアウトに影響しない box-shadow）
          '&.Mui-selected': {
            boxShadow: `inset 0 -2px 0 ${theme.palette.primary.main}`,
          },
        }),
      },
    },
    MuiToolbar: {
      defaultProps: {
        variant: 'dense',
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    // v9 のピッカーは MuiTextField とは別物（MuiPickersTextField）。
    // デフォルトを small にして高さをコンパクトに揃える。
    MuiPickersTextField: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiLink: {
      defaultProps: {
        color: 'primary',
        component: LinkBehavior,
      } as LinkProps,
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
        LinkComponent: LinkBehavior,
      },
      variants: [
        {
          props: {
            variant: 'contained',
            color: 'success',
            disableElevation: true,
          },
          style: {
            color: '#fff',
          },
        },
        {
          props: {
            variant: 'contained',
            color: 'warning',
            disableElevation: true,
          },
          style: {
            color: '#fff',
          },
        },
        {
          props: {
            variant: 'contained',
            color: 'error',
            disableElevation: true,
          },
          style: {
            color: '#fff',
          },
        },
        {
          props: {
            variant: 'contained',
            color: 'standard',
            disableElevation: true,
          },
          style: {
            color: '#fff',
          },
        },
        {
          props: {
            variant: 'contained',
            color: 'secondary',
            disableElevation: true,
          },
          style: {
            backgroundColor: '#fff',
            '&:hover': {
              backgroundColor: blueGrey['25'],
            },
          },
        },
        {
          props: {
            variant: 'outlined',
            color: 'standard',
            disableElevation: true,
          },
          style: {
            backgroundColor: '#fff',
            borderColor: blueGrey['75'],
            '&:hover': {
              borderColor: blueGrey['100'],
            },
          },
        },
        {
          props: {
            variant: 'outlined',
            color: 'primary',
            disableElevation: true,
          },
          style: {
            backgroundColor: '#fff',
          },
        },
        {
          props: { variant: 'contained', disableElevation: true },
          style: {},
        },
      ],
    },
    MuiIconButton: {
      defaultProps: {
        color: 'standard',
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderColor: blueGrey[200],
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        sx: { pb: 0 },
        slotProps: {
          title: { variant: 'subtitle1' },
          subheader: { variant: 'subtitle2' },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: blueGrey['75'],
          },
          '&.Mui-disabled fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: blueGrey['75'],
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: '#fff',
        },
        filled: {
          backgroundColor: '#fff',
        },
      },
      defaultProps: {
        size: 'small',
      },
    },
    MuiTypography: {
      styleOverrides: {
        body1: {
          marginBottom: '-1px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: blueGrey['800'],
          borderColor: blueGrey['75'],
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          color: blueGrey['800'],
          borderColor: blueGrey['75'],
          boxSizing: 'border-box',
        },
        paper: {
          backgroundColor: blueGrey['25'],
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0 24px 16px',
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: blueGrey['800'],
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          color: blueGrey['800'],
        },
        primary: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiLinearProgress: {
      defaultProps: {
        color: 'info',
      },
    },
    MuiCircularProgress: {
      variants: [
        {
          props: {
            variant: 'indeterminate',
            color: 'primary',
          },
          style: {
            color: blueGrey['500'],
          },
        },
      ],
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        select: {
          // ページサイズの選択ボックスの文字が上ずれるのを修正
          '&.MuiInputBase-inputSizeSmall': {
            paddingTop: 2,
            paddingBottom: 2,
          },
        },
      },
    },
  },
}

export const globalStyles: GlobalStylesProps['styles'] = {
  // 画面全体を90%スケールで表示（100%表示でも90%相当の大きさに見せる）
  // body ではなく #root に適用することで、body 直下に描画される
  // MUI の Popover / Menu / Select などのポップアップ位置がズレないようにする
  '#root': {
    zoom: 0.9,
  },
  // スクロールバーの全体
  '*::-webkit-scrollbar': {
    width: 8,
    height: 6,
  },
  // スクロールバーのつまみ
  '*::-webkit-scrollbar-thumb': {
    // backgroundColor: 'transparent',
    backgroundColor: '#00000022',
    borderRadius: 5,
  },
  // スクロールバーの背景
  '*::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  // 水平と垂直のスクロールバーの交差部分
  '*::-webkit-scrollbar-corner': {
    display: 'none',
  },
  // サイズ変更用のつまみ
  '*::-webkit-resizer': {
    display: 'none',
  },
  // スクロールバーの矢印ボタン
  '*::-webkit-scrollbar-button': {
    display: 'none',
  },
  // スクロールバーの無効
  '.no-scrollbar': {
    '*::-webkit-scrollbar': {
      display: 'none',
    },
  },
}

const canalyLightTheme = responsiveFontSizes(
  createTheme(canalyLightThemeOptions, customPalette, coreJaJp, dataGridJaJp, datePickerJaJp)
)

/**
 * ダークモード用のパレット上書き。
 * ライトテーマをベースに、背景・テキスト・ボーダー等を暗色系へ差し替える。
 */
const darkPaletteOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    standard: {
      light: '#1f232a',
      main: '#e4e7eb',
    },
    secondary: {
      light: blueGrey['300'],
      main: '#272b33',
      dark: blueGrey['500'],
      contrastText: '#e4e7eb',
    },
    border: {
      light: '#ffffff1a',
      main: '#ffffff24',
      dark: '#ffffff40',
    },
    background: {
      default: '#16181d',
      paper: '#1f232a',
    },
    text: {
      primary: '#e4e7eb',
      secondary: '#a7b0ba',
    },
  },
}

/**
 * ダークモードで、ライトテーマ側にハードコードされている白背景・濃色文字を打ち消す上書き。
 * createTheme の deep-merge により、ライトの styleOverrides に対して該当キーのみ差し替える。
 */
const darkComponentOptions: ThemeOptions = {
  components: {
    MuiSelect: {
      styleOverrides: {
        select: { backgroundColor: 'transparent' },
        filled: { backgroundColor: 'transparent' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { color: '#e4e7eb', borderColor: '#ffffff24' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: { color: '#e4e7eb', borderColor: '#ffffff24' },
        paper: { backgroundColor: '#1f232a' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderColor: '#ffffff24' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff33' },
          '&.Mui-disabled fieldset.MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff24' },
        },
      },
    },
  },
}

const canalyDarkTheme = responsiveFontSizes(
  createTheme(
    canalyLightThemeOptions,
    customPalette,
    darkPaletteOptions,
    darkComponentOptions,
    coreJaJp,
    dataGridJaJp,
    datePickerJaJp
  )
)

// MUI v7 のテーマに存在する color ヘルパー `theme.alpha()` を v6 テーマへポリフィル。
// @mui/x-scheduler（v7 前提）が `theme.alpha(...)` を呼ぶため、v6 でも動くように補う。
;(canalyLightTheme as unknown as { alpha: typeof alpha }).alpha = alpha
;(canalyDarkTheme as unknown as { alpha: typeof alpha }).alpha = alpha

const darkGlobalStyles: GlobalStylesProps['styles'] = {
  ...globalStyles,
  '*::-webkit-scrollbar-thumb': {
    backgroundColor: '#ffffff33',
    borderRadius: 5,
  },
}

/**
 * カラーモード（jotai の colorModeAtom）に応じてライト / ダークテーマを切り替える。
 */
export function CanalyTheme({ children }: { children?: ReactNode }) {
  const mode = useAtomValue(colorModeAtom)
  const isDark = mode === 'dark'

  return (
    <ThemeProvider theme={isDark ? canalyDarkTheme : canalyLightTheme}>
      <GlobalStyles styles={isDark ? darkGlobalStyles : globalStyles} />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={jaLocale}>
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  )
}

/**
 * @deprecated CanalyTheme を使用してください（カラーモード対応）。
 */
export function CanalyLightTheme({ children }: { children?: ReactNode }) {
  return <CanalyTheme>{children}</CanalyTheme>
}
